import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { PhoneInput } from '@bambi/component-library/src/components/inputs/PhoneInput';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { useFormContext } from 'react-hook-form';

import { useAppSelector } from 'src/app/store/hooks';
import { FormField } from 'src/common/FormField';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import { ManagePaymentMethods } from 'src/common/forms/PaymentMethodCaptureForm/ManagePaymentMethods';
import { useCanCapturePaymentMethod } from 'src/common/forms/PaymentMethodCaptureForm/useCanCapturePaymentMethod';

import { FormValues } from './FormValues';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

export const PayerBillingInformation = () => {
  const { selectedPayer } = useAppSelector((state) => state.payer);
  const { clearErrors, getValues } = useFormContext<FormValues>();

  const {
    isEnabled: hasPaymentCaptureFeature,
    isLoading: isLoadingPaymentCaptureFeature,
  } = useCanCapturePaymentMethod();

  return (
    <>
      <div className="flex flex-col gap-3 py-4">
        <div>
          <h2 className="text-xl">Billing Information</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <FormFieldController name="billing_name">
                {({ field, fieldState }) => {
                  return (
                    <FormField
                      label="Billing Contact Name"
                      error={fieldState.error?.message?.toString()}
                      type="text"
                      inputProps={{
                        id: 'billing_name',
                        ...field,
                      }}
                      value={field.value}
                    />
                  );
                }}
              </FormFieldController>
            </div>
            <div>
              <FormFieldController name="billing_address">
                {({ field, fieldState }) => {
                  return (
                    <FormField
                      label="Billing Address"
                      type="location-typeahead"
                      inputProps={{
                        id: 'billing-address',
                      }}
                      value={field.value}
                      onChange={(option: SelectOption) => {
                        // setValue doesn't trigger revalidation for some reason
                        clearErrors('billing_address');
                        field.onChange({
                          location: { place_id: option.value },
                          label: option.label,
                        });
                      }}
                      options={
                        selectedPayer?.billing_address
                          ? [
                              {
                                label:
                                  selectedPayer.billing_address.location
                                    ?.label || '',
                                value:
                                  selectedPayer.billing_address.location
                                    ?.place_id || '',
                              },
                            ]
                          : []
                      }
                      error={
                        field.errors?.billing_address?.place_id?.message &&
                        !field.value?.place_id
                          ? 'Please select a valid location'
                          : undefined
                      }
                    />
                  );
                }}
              </FormFieldController>
            </div>
            <div>
              <FormFieldController name={'billing_phone_number'}>
                {({ field, fieldState }) => {
                  return (
                    <div>
                      <DefaultFormFieldLayout
                        label="Billing Phone Number"
                        inputId="billing_phone_number"
                      >
                        <PhoneInput
                          {...field}
                          placeholder="(555) 555-5555"
                          dataTestId="phone"
                        />
                      </DefaultFormFieldLayout>
                    </div>
                  );
                }}
              </FormFieldController>
            </div>
            <div>
              <FormFieldController name="billing_email">
                {({ field, fieldState }) => {
                  return (
                    <FormField
                      label="Billing Email"
                      error={fieldState.error?.message?.toString()}
                      type="email"
                      inputProps={{
                        id: 'billing_email',
                        ...field,
                      }}
                      value={field.value}
                    />
                  );
                }}
              </FormFieldController>
            </div>
            <div>
              <FormFieldController name={'invoice_due_after_days'}>
                {({ field, fieldState }) => {
                  return (
                    <FormField
                      label="Invoice Due After (Days)"
                      error={fieldState.error?.message?.toString()}
                      type="number"
                      inputProps={{
                        id: 'invoice_due_after_days',
                        ...field,
                      }}
                      value={field.value}
                    />
                  );
                }}
              </FormFieldController>
            </div>
          </div>
        </div>
      </div>
      {isLoadingPaymentCaptureFeature ? (
        <LoadingIndicator testId="loading-payment-capture" />
      ) : (
        hasPaymentCaptureFeature && (
          <div className="pt-4">
            <FormFieldController name="billing_details">
              {({ field }) => {
                return (
                  <ManagePaymentMethods
                    payerId={getValues().id}
                    payerType="payer_organization"
                    billingName={field.value?.billingName}
                    onChangeBillingDetails={field.onChange}
                    creditCardFormClassName="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                    billingAddressFormClassName="grid grid-cols-1 md:grid-cols-2 gap-4"
                    paymentMethodListClassName="grid grid-cols-1 md:grid-cols-3 gap-3"
                  />
                );
              }}
            </FormFieldController>
          </div>
        )
      )}
    </>
  );
};
